import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Spinner } from 'react-bootstrap';
import { ExternalLink, MapPin } from 'lucide-react';
import { getDatabase, ref, get } from 'firebase/database';
import { Building, BuildingSummary, BuildingInfo } from '../types/protocols';
import { BaseComponentImpl } from '../types/implementations';
import { resolveComponentLocation } from '../Helpers/ComponentRoomUtils';
import { findBlueprintImage } from '../Helpers/blueprintLookup';
import { photoLocationLabel, photoLocationList } from '../Helpers/photoLocations';
import BlueprintWithMarkers from './BlueprintWithMarkers';
import PhotoWithMarker from './PhotoWithMarker';
import MiniMap from './MiniMap';

interface LocationSectionProps {
    buildingKey: string;
    buildingSummary?: BuildingSummary;
    component?: BaseComponentImpl | null;
    building?: Building | null;
    showVisualMarkers?: boolean;
    showMap?: boolean;
    compact?: boolean;
    /** Show building-level info (kommun, address, mini-map, building name). Turn
     *  off for per-component blocks so the shared building info isn't repeated. */
    showBuildingInfo?: boolean;
    /** Rendered when there is no location data to show (e.g. a component with no
     *  markers, level, or room). Lets callers show a placeholder instead of
     *  nothing. Only shown once the building data needed to resolve location has
     *  loaded, so it never flashes before markers/room appear. */
    emptyPlaceholder?: React.ReactNode;
}

/**
 * Format address from BuildingInfo, omitting missing fields gracefully
 */
function formatAddress(info: BuildingInfo | undefined): string | null {
    if (!info) return null;

    const parts: string[] = [];
    if (info.street) parts.push(info.street);

    const cityParts: string[] = [];
    if (info.postalCode) cityParts.push(info.postalCode);
    if (info.city) cityParts.push(info.city);

    if (cityParts.length > 0) {
        parts.push(cityParts.join(' '));
    }

    return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Generate Google Maps URL
 * Prefers coordinates if available, falls back to address search
 */
function getGoogleMapsUrl(
    coordinates: { latitude: number; longitude: number } | undefined,
    info: BuildingInfo | undefined
): string | null {
    if (coordinates?.latitude && coordinates?.longitude) {
        return `https://maps.google.com/?q=${coordinates.latitude},${coordinates.longitude}`;
    }

    const address = formatAddress(info);
    if (address) {
        return `https://maps.google.com/search/${encodeURIComponent(address)}`;
    }

    return null;
}

/**
 * Shared component for displaying location information in modals.
 * Shows municipality, address (with Google Maps link), building hierarchy,
 * and optional visual markers (blueprint, satellite, photo).
 */
const LocationSection: React.FC<LocationSectionProps> = ({
    buildingKey,
    buildingSummary,
    component,
    building,
    showVisualMarkers = true,
    showMap = true,
    compact = false,
    showBuildingInfo = true,
    emptyPlaceholder
}) => {
    const [buildingInfo, setBuildingInfo] = useState<BuildingInfo | null>(null);
    const [loadingInfo, setLoadingInfo] = useState(false);
    const [fullBuilding, setFullBuilding] = useState<Building | null>(building || null);
    const [loadingBuilding, setLoadingBuilding] = useState(false);

    // Fetch BuildingInfo if not available in summary
    useEffect(() => {
        if (!buildingKey) return;

        // BuildingInfo should be in buildingSummary.buildingInfo
        if (buildingSummary?.buildingInfo) {
            setBuildingInfo(buildingSummary.buildingInfo);
            return;
        }

        // Fallback: fetch from Firebase
        const fetchBuildingInfo = async () => {
            setLoadingInfo(true);
            try {
                const db = getDatabase();
                const infoRef = ref(db, `buildingsSummary/${buildingKey}/buildingInfo`);
                const snapshot = await get(infoRef);
                if (snapshot.exists()) {
                    setBuildingInfo(snapshot.val() as BuildingInfo);
                }
            } catch (error) {
                console.error('Error fetching building info:', error);
            } finally {
                setLoadingInfo(false);
            }
        };

        fetchBuildingInfo();
    }, [buildingKey, buildingSummary?.buildingInfo]);

    // Fetch full building data for visual markers if needed
    useEffect(() => {
        if (!buildingKey || !showVisualMarkers || !component) return;
        if (building) {
            setFullBuilding(building);
            return;
        }

        const fetchBuilding = async () => {
            setLoadingBuilding(true);
            try {
                const db = getDatabase();
                const buildingPath = `buildings/${buildingKey}`;

                const [
                    summarySnapshot,
                    structureSnapshot,
                    mapImagesSnapshot,
                    currentMapKeySnapshot
                ] = await Promise.all([
                    get(ref(db, `buildingsSummary/${buildingKey}`)),
                    get(ref(db, `${buildingPath}/structure`)),
                    get(ref(db, `${buildingPath}/mapImages`)),
                    get(ref(db, `${buildingPath}/currentMapImageKey`))
                ]);

                if (summarySnapshot.exists()) {
                    const summary = summarySnapshot.val() as BuildingSummary;
                    const bld: Building = {
                        key: buildingKey,
                        model: {
                            key: buildingKey,
                            structure: structureSnapshot.exists() ? structureSnapshot.val() : undefined,
                            mapImages: mapImagesSnapshot.exists() ? mapImagesSnapshot.val() : undefined,
                            currentMapImageKey: currentMapKeySnapshot.exists() ? currentMapKeySnapshot.val() : undefined
                        },
                        summary
                    };
                    setFullBuilding(bld);
                }
            } catch (error) {
                console.error('Error fetching building for location markers:', error);
            } finally {
                setLoadingBuilding(false);
            }
        };

        fetchBuilding();
    }, [buildingKey, building, showVisualMarkers, component]);

    // Resolve room/level from component
    const locationInfo = useMemo(() => {
        if (!component || !fullBuilding?.model?.structure) {
            return null;
        }
        return resolveComponentLocation(component, fullBuilding.model.structure);
    }, [component, fullBuilding?.model?.structure]);

    // Determine available location markers
    const photoPins = useMemo(() => (component ? photoLocationList(component, fullBuilding) : []), [component, fullBuilding]);
    const hasPhotoLocation = photoPins.length > 0;
    const hasMapLocations = component?.mapLocations && Object.keys(component.mapLocations).length > 0;
    const hasBlueprintLocations = component?.blueprintLocations && Object.keys(component.blueprintLocations).length > 0;
    const hasAnyMarker = hasPhotoLocation || hasMapLocations || hasBlueprintLocations;

    // Current map image for satellite view
    const currentMapImageKey = fullBuilding?.model?.currentMapImageKey;
    const mapImage = currentMapImageKey && fullBuilding?.model?.mapImages?.[currentMapImageKey];

    // Address and Google Maps link
    const address = formatAddress(buildingInfo ?? buildingSummary?.buildingInfo);
    const googleMapsUrl = getGoogleMapsUrl(
        buildingSummary?.coordinates,
        buildingInfo ?? buildingSummary?.buildingInfo
    );
    const municipality = buildingInfo?.municipality ?? buildingSummary?.buildingInfo?.municipality;

    const buildingName = buildingSummary?.name || fullBuilding?.summary?.name;
    const buildingPageUrl = `/building/${buildingKey}`;

    // Show loading state if fetching critical data
    if (loadingInfo) {
        return (
            <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <span className="ms-2 small text-muted">Laddar platsdata...</span>
            </div>
        );
    }

    const hasCoordinates = buildingSummary?.coordinates?.latitude && buildingSummary?.coordinates?.longitude;

    // Don't render if no location data at all
    const hasBuildingBlock = showBuildingInfo && !!(municipality || address || (showMap && hasCoordinates) || (buildingName && !compact));
    const hasAnyData = hasBuildingBlock || locationInfo?.levelDisplay || locationInfo?.roomDisplay || (showVisualMarkers && hasAnyMarker);
    if (!hasAnyData) {
        // Still fetching the building data needed to resolve markers/room — show
        // a loading hint rather than prematurely concluding there's nothing.
        if (showVisualMarkers && loadingBuilding) {
            return (
                <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2 small text-muted">Laddar platsmarkörer...</span>
                </div>
            );
        }
        if (emptyPlaceholder) {
            return <div className={compact ? '' : 'mb-3'}>{emptyPlaceholder}</div>;
        }
        return null;
    }

    return (
        <div className={compact ? '' : 'mb-3'}>
            {/* Map and Address side by side */}
            {showBuildingInfo && (showMap && hasCoordinates ? (
                <Row className="mb-2">
                    <Col xs={12} md={6} className="mb-2 mb-md-0">
                        {municipality && (
                            <div className="text-muted small mb-1">
                                <strong>Kommun:</strong> {municipality}
                            </div>
                        )}
                        {address && (
                            <div className="d-flex align-items-center gap-1">
                                <MapPin size={14} className="text-muted" />
                                {googleMapsUrl ? (
                                    <a
                                        href={googleMapsUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="small"
                                    >
                                        {address}
                                        <ExternalLink size={10} className="ms-1" style={{ verticalAlign: 'middle' }} />
                                    </a>
                                ) : (
                                    <span className="small">{address}</span>
                                )}
                            </div>
                        )}
                    </Col>
                    <Col xs={12} md={6}>
                        <MiniMap
                            coordinates={buildingSummary!.coordinates!}
                            buildingName={buildingSummary?.name}
                            height={compact ? 150 : 180}
                        />
                    </Col>
                </Row>
            ) : (municipality || address) ? (
                <div className="mb-2">
                    {municipality && (
                        <div className="text-muted small mb-1">
                            <strong>Kommun:</strong> {municipality}
                        </div>
                    )}
                    {address && (
                        <div className="d-flex align-items-center gap-1">
                            <MapPin size={14} className="text-muted" />
                            {googleMapsUrl ? (
                                <a
                                    href={googleMapsUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="small"
                                >
                                    {address}
                                    <ExternalLink size={10} className="ms-1" style={{ verticalAlign: 'middle' }} />
                                </a>
                            ) : (
                                <span className="small">{address}</span>
                            )}
                        </div>
                    )}
                </div>
            ) : null)}

            {/* Building Name with Link */}
            {showBuildingInfo && buildingName && !compact && (
                <div className="mb-2">
                    <strong className="small text-muted">Byggnad:</strong>{' '}
                    <a
                        href={buildingPageUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="small"
                    >
                        {buildingName}
                        <ExternalLink size={10} className="ms-1" style={{ verticalAlign: 'middle' }} />
                    </a>
                </div>
            )}

            {/* Level & Room */}
            {(locationInfo?.levelDisplay || locationInfo?.roomDisplay) && (
                <div className="small text-muted mb-2">
                    {locationInfo.levelDisplay && (
                        <span><strong>Våning:</strong> {locationInfo.levelDisplay}</span>
                    )}
                    {locationInfo.levelDisplay && locationInfo.roomDisplay && (
                        <span className="mx-2">•</span>
                    )}
                    {locationInfo.roomDisplay && (
                        <span><strong>Rum:</strong> {locationInfo.roomDisplay}</span>
                    )}
                </div>
            )}

            {/* Visual Markers */}
            {showVisualMarkers && hasAnyMarker && !loadingBuilding && fullBuilding && (
                <div className={compact ? 'mt-2' : 'mt-3 pt-2 border-top'}>
                    {!compact && (
                        <div className="small text-muted mb-2 fw-medium">Platsdata</div>
                    )}
                    <Row>
                        {/* Photo locations: one per pinned parent-surface photo, primary pin first */}
                        {photoPins.map(({ photoKey, location, side }) => (
                            <Col md={6} className="mb-3" key={photoKey}>
                                <PhotoWithMarker
                                    photoLocation={location}
                                    title={photoLocationLabel(side)}
                                />
                            </Col>
                        ))}

                        {/* Map/Satellite Locations */}
                        {hasMapLocations && mapImage && currentMapImageKey &&
                            component!.mapLocations![currentMapImageKey] && (
                                <Col md={6} className="mb-3">
                                    <BlueprintWithMarkers
                                        blueprint={mapImage}
                                        currentBlueprintKey={currentMapImageKey}
                                        building={fullBuilding}
                                        pointMarker={component!.mapLocations![currentMapImageKey]}
                                        pointMarkerLabel="Kartposition"
                                        title="Kartposition"
                                    />
                                </Col>
                            )}

                        {/* Blueprint Locations */}
                        {hasBlueprintLocations && fullBuilding &&
                            Object.entries(component!.blueprintLocations!).map(([blueprintKey, location]) => {
                                // A level's drawing or the roof drawing; only the owner's current drawing is shown
                                const found = findBlueprintImage(fullBuilding.model?.structure, blueprintKey);
                                if (!found?.isCurrent) return null;

                                return (
                                    <Col md={6} className="mb-3" key={`blueprint-${blueprintKey}`}>
                                        <BlueprintWithMarkers
                                            blueprint={found.image}
                                            currentBlueprintKey={blueprintKey}
                                            building={fullBuilding}
                                            pointMarker={location}
                                            pointMarkerLabel="Ritningsposition"
                                            title={found.owner.kind === 'roofPlan' ? 'Position på takplan' : 'Ritningsposition'}
                                        />
                                    </Col>
                                );
                            })
                        }
                    </Row>
                </div>
            )}

            {/* Loading state for visual markers */}
            {showVisualMarkers && loadingBuilding && (
                <div className="text-center py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="ms-2 small text-muted">Laddar platsmarkörer...</span>
                </div>
            )}
        </div>
    );
};

export default LocationSection;
